
import React, { useState } from 'react';

interface FileItem {
  id: string;
  name: string;
  type: 'pdf' | 'doc' | 'img' | 'xls';
  size: string;
  uploadedBy: string;
  date: string;
}

const MOCK_FILES: FileItem[] = [
  { id: '1', name: 'Housing_Policy_2024.pdf', type: 'pdf', size: '2.4 MB', uploadedBy: 'Management', date: '22 Oct 2024' },
  { id: '2', name: 'Staff_Handbook_V2.pdf', type: 'pdf', size: '5.1 MB', uploadedBy: 'HR Admin', date: '15 Oct 2024' },
  { id: '3', name: 'Site_Map_Hatfield.img', type: 'img', size: '12.8 MB', uploadedBy: 'Operations', date: '10 Oct 2024' },
  { id: '4', name: 'Rota_Template_2024.xls', type: 'xls', size: '1.2 MB', uploadedBy: 'Hayley Isacc', date: '05 Oct 2024' },
  { id: '5', name: 'Safeguarding_Guidance.doc', type: 'doc', size: '840 KB', uploadedBy: 'Compliance', date: '01 Oct 2024' },
];

const FileStorageView: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'shared' | 'personal'>('all');

  return (
    <div className="space-y-12 pb-16">
      {/* Brand Header */}
      <div className="section-header flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h2 className="ymca-brand-font text-6xl uppercase leading-none">Resource Hub</h2>
          <p className="text-gray-500 font-medium uppercase text-xs tracking-[0.2em] mt-2">Shared File Storage & Operational Assets</p>
        </div>
        <div className="flex gap-4">
          <button className="bg-black text-white px-8 py-4 font-bold text-xs uppercase tracking-widest hover:bg-ymca-orange transition-all">
            Upload Asset
          </button>
          <button className="border-2 border-black px-8 py-4 font-bold text-xs uppercase tracking-widest hover:bg-gray-100 transition-all">
            New Folder
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1 space-y-8">
          <div className="space-y-2">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 mb-4">Categories</h3>
            <NavItem label="All Resources" active={filter === 'all'} onClick={() => setFilter('all')} />
            <NavItem label="Shared with me" active={filter === 'shared'} onClick={() => setFilter('shared')} />
            <NavItem label="My Documents" active={filter === 'personal'} onClick={() => setFilter('personal')} />
            <NavItem label="Recent Activity" />
            <NavItem label="Archive" />
          </div>

          <div className="bg-ymca-neutral p-6 space-y-4 border-l-4 border-black">
             <h4 className="ymca-brand-font text-2xl uppercase">Storage Status</h4>
             <div className="space-y-2">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                   <span>Used Capacity</span>
                   <span>42%</span>
                </div>
                <div className="h-1 bg-gray-200 w-full">
                   <div className="h-full bg-black w-[42%]"></div>
                </div>
                <p className="text-[10px] text-gray-500 font-medium">8.4 GB of 20 GB used</p>
             </div>
          </div>
        </div>

        {/* File List Area */}
        <div className="lg:col-span-3 space-y-6">
          <div className="flex justify-between items-center bg-gray-50 p-4 border border-black/5">
             <div className="flex items-center space-x-4">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sort by:</span>
                <select className="bg-transparent text-[10px] font-black uppercase tracking-widest focus:ring-0 cursor-pointer">
                   <option>Date Modified</option>
                   <option>File Name</option>
                   <option>Size</option>
                </select>
             </div>
             <div className="flex space-x-2">
                <button className="p-2 border border-black/10 bg-white">
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" /></svg>
                </button>
                <button className="p-2 border border-black bg-black text-white">
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeWidth={2} d="M4 6h6v6H4V6zm10 0h6v6h-6V6zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" /></svg>
                </button>
             </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {MOCK_FILES.map(file => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const NavItem = ({ label, active, onClick }: any) => (
  <button 
    onClick={onClick}
    className={`w-full text-left px-4 py-3 font-bold text-xs uppercase tracking-widest transition-all border-l-4 ${active ? 'bg-black text-white border-ymca-orange' : 'text-gray-500 hover:bg-gray-50 border-transparent'}`}
  >
    {label}
  </button>
);

// Fix: Use React.FC type to handle component props properly, resolving the error where 'key' was not expected on the props object.
const FileCard: React.FC<{ file: FileItem }> = ({ file }) => {
  const getIcon = () => {
    switch (file.type) {
      case 'pdf': return <PDFIcon />;
      case 'doc': return <DOCIcon />;
      case 'xls': return <XLSIcon />;
      case 'img': return <IMGIcon />;
    }
  };

  return (
    <div className="border-2 border-black p-6 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all group cursor-default">
      <div className="flex justify-between items-start mb-6">
        <div className="bg-black p-3 text-white">
           {getIcon()}
        </div>
        <button className="p-1 text-gray-400 hover:text-black transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg>
        </button>
      </div>
      <h4 className="ymca-brand-font text-2xl uppercase truncate mb-1" title={file.name}>{file.name}</h4>
      <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">
        <span>{file.size}</span>
        <span>{file.date}</span>
      </div>
      <div className="pt-4 border-t border-black/5 flex items-center justify-between">
         <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-ymca-neutral border border-black/10 flex items-center justify-center text-[8px] font-black">
               {file.uploadedBy[0]}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-tighter text-gray-500">{file.uploadedBy}</span>
         </div>
         <button className="text-[10px] font-black uppercase text-black hover:text-ymca-orange underline underline-offset-4">Download</button>
      </div>
    </div>
  );
};

const PDFIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /><path strokeLinecap="square" strokeWidth={2} d="M9 17h6M9 13h6" /></svg>
);

const DOCIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);

const XLSIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
);

const IMGIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
);

export default FileStorageView;
